import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Custom hook that tracks which section is currently in view using Intersection Observer.
 * Used by SidebarSettingsLayout for scrollspy navigation.
 *
 * @param {string[]} sectionIds - Array of section element IDs to observe
 * @param {Object} options
 * @param {string} options.rootMargin - Intersection Observer rootMargin (default: "0px 0px -60% 0px")
 * @param {number} options.threshold - Intersection Observer threshold (default: 0)
 * @returns {{ activeSection: string, scrollToSection: (id: string) => void }}
 */
const useScrollSpy = (sectionIds, options = {}) => {
  const { rootMargin = "0px 0px -60% 0px", threshold = 0 } = options;
  const [activeSection, setActiveSection] = useState(sectionIds[0] || "");
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    if (!sectionIds.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Don't update during programmatic scrolling
        if (isScrollingRef.current) return;

        // Find the topmost visible section
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          // Sort by position on the page (top to bottom)
          visibleEntries.sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          );
          setActiveSection(visibleEntries[0].target.id);
        }
      },
      { rootMargin, threshold }
    );

    // Observe all section elements
    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [sectionIds, rootMargin, threshold]);

  const scrollToSection = useCallback(
    (id) => {
      const element = document.getElementById(id);
      if (!element) return;

      // Disable observer updates during programmatic scroll
      isScrollingRef.current = true;
      setActiveSection(id);

      // Use manual offset to respect sticky header height (scrollIntoView ignores scroll-margin on some browsers)
      const headerOffset = window.innerWidth < 768 ? 120 : 68;
      const top = element.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });

      // Update URL hash without triggering scroll
      if (window.history.replaceState) {
        window.history.replaceState(null, null, `#${id}`);
      }

      // Re-enable observer after scroll completes
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 1000);
    },
    []
  );

  // Handle initial hash on mount
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash && sectionIds.includes(hash)) {
      // Small delay to ensure DOM is ready
      const timeout = setTimeout(() => {
        scrollToSection(hash);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [sectionIds, scrollToSection]);

  return { activeSection, scrollToSection };
};

export default useScrollSpy;
